/**
 * Custom: Theme fixes
 * -----------------------------------------------------------------------------
 * Runtime patches for behaviour in the compiled Canvas components
 * (`component.*.js`), whose Vue source is not in this repo.
 *
 * 1. Tabbed info history. `tabbed-info` writes the active tab to `?tab=` with
 *    `history.pushState`, including the default tab as soon as the section
 *    scrolls into view. Every product page therefore gained an extra history
 *    entry, and pressing Back landed on the bare product URL, which pushed
 *    `?tab=` again, so Back could not leave the page. A URL change that only
 *    touches the `tab` parameter now replaces the current entry instead.
 * 2. Header accessibility. The account trigger in the promo banner is a plain
 *    `<div>` with a click handler, so it cannot be reached or used by
 *    keyboard, and the mobile cart link holds only an icon and a count, so it
 *    has no accessible name. Both get the missing attributes once Vue renders
 *    them, and Enter or Space on the account trigger clicks it.
 */
(function() {
  'use strict'

  var TAB_PARAMETER = 'tab'
  var ACCOUNT_SELECTOR = '.promo-banner__account'
  var CART_LINK_SELECTOR = 'a.site-header__cart'

  /**
   * Check whether moving to `url` changes nothing but the `tab` parameter.
   * @param {String|URL} url - URL passed to `pushState`.
   * @returns {Boolean}
   */
  function isTabOnlyChange(url) {
    if (url === undefined || url === null) {
      return false
    }

    var current
    var next

    try {
      current = new URL(window.location.href)
      next = new URL(String(url), current)
    } catch (error) {
      return false
    }

    if (
      next.origin !== current.origin ||
      next.pathname !== current.pathname ||
      next.hash !== current.hash ||
      next.searchParams.get(TAB_PARAMETER) === null
    ) {
      return false
    }

    current.searchParams.delete(TAB_PARAMETER)
    next.searchParams.delete(TAB_PARAMETER)

    return current.search === next.search
  }

  var pushState = window.history.pushState

  window.history.pushState = function(state, title, url) {
    if (isTabOnlyChange(url)) {
      return window.history.replaceState(state, title, url)
    }

    return pushState.apply(window.history, arguments)
  }

  /**
   * Read a theme string, falling back when the strings are not loaded.
   * @param {String} group - Key under `general.site_header`.
   * @returns {String}
   */
  function headerString(group) {
    var strings = window.cnvs && (window.cnvs.compiledStrings || window.cnvs.strings)
    var header = strings && strings.general && strings.general.site_header

    return (header && header[group]) || ''
  }

  /**
   * Add the missing roles and names to the header elements in `root`.
   * @param {ParentNode} root - Element to search.
   */
  function labelHeader(root) {
    var accounts = root.querySelectorAll(ACCOUNT_SELECTOR + ':not([tabindex])')
    var cartLinks = root.querySelectorAll(CART_LINK_SELECTOR + ':not([aria-label])')

    Array.prototype.forEach.call(accounts, function(element) {
      element.setAttribute('role', 'button')
      element.setAttribute('tabindex', '0')
    })

    Array.prototype.forEach.call(cartLinks, function(element) {
      var label = headerString('cart')

      if (label) {
        element.setAttribute('aria-label', label)
      }
    })
  }

  document.addEventListener('keydown', function(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    var target = event.target

    if (!target || !target.matches || !target.matches(ACCOUNT_SELECTOR)) {
      return
    }

    event.preventDefault()
    target.click()
  })

  /**
   * Vue replaces the server-rendered header after load and re-renders it on
   * state changes, so watch for new nodes rather than labelling once.
   */
  function watchHeader() {
    var scheduled = false

    labelHeader(document)

    var observer = new MutationObserver(function() {
      if (scheduled) {
        return
      }

      scheduled = true

      window.requestAnimationFrame(function() {
        scheduled = false
        labelHeader(document)
      })
    })

    observer.observe(document.body, { childList: true, subtree: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchHeader)
  } else {
    watchHeader()
  }
})()
